import React, { useState, useEffect, useRef } from 'react';
import { TextField, Box, List, ListItem, ListItemText, Paper, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_MAPS_API_KEY } from '../utils/constants';

const GooglePlacesAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Search for a location...",
  error = false,
  helperText = "",
  label = "Location",
  onMapOpen
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPredictions, setShowPredictions] = useState(false);
  const inputRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const googleMaps = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const initializeGoogleMaps = async () => {

      try {
        const loader = new Loader({
          apiKey: GOOGLE_MAPS_API_KEY,
          version: 'weekly',
          libraries: ['places', 'marker']
        });

        const google = await loader.load();
        googleMaps.current = google;

        // Create autocomplete service
        autocompleteService.current = new google.maps.places.AutocompleteService();

        // Create places service
        placesService.current = new google.maps.places.PlacesService(
          document.createElement('div')
        );
      } catch (error) {
        console.error('Error loading Google Maps:', error);
      }
    };

    initializeGoogleMaps();
  }, []);

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange(newValue);

    if (newValue.length > 2 && autocompleteService.current) {
      setIsLoading(true);
      autocompleteService.current.getPlacePredictions(
        {
          input: newValue,
          types: ['geocode', 'establishment']
        },
        (predictions, status) => {
          setIsLoading(false);
          if (status === googleMaps.current?.maps?.places?.PlacesServiceStatus?.OK && predictions) {
            setPredictions(predictions);
            setShowPredictions(true);
          } else {
            setPredictions([]);
            setShowPredictions(false);
          }
        }
      );
    } else {
      setPredictions([]);
      setShowPredictions(false);
    }
  };

  const handlePredictionClick = (prediction) => {
    setInputValue(prediction.description);
    setShowPredictions(false);
    onChange(prediction.description);

    // Get detailed place information
    if (placesService.current) {
      placesService.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['formatted_address', 'geometry', 'address_components']
        },
        (place, status) => {
          console.log("Full Places Details API Response:", place);
          if (status === googleMaps.current?.maps?.places?.PlacesServiceStatus?.OK && place) {
            // Extract address components
            const addressComponents = place.address_components || [];
            const addressData = {
              formatted_address: place.formatted_address,
              lat: place.geometry?.location?.lat(),
              lng: place.geometry?.location?.lng(),
              address: '',
              city: '',
              postalCode: '',
              country: ''
            };

            let building = '';
            let route = '';
            let sublocality1 = '';
            let sublocality2 = '';
            let sublocality3 = '';
            let neighborhood = '';

            // Improved Parsing Logic
            addressComponents.forEach(component => {
              const types = component.types;
              if (types.includes('premise') || types.includes('subpremise') || types.includes('street_number')) {
                building = (building ? building + ', ' : '') + component.long_name;
              } else if (types.includes('route')) {
                route = component.long_name;
              } else if (types.includes('neighborhood')) {
                neighborhood = component.long_name;
              } else if (types.includes('sublocality_level_3')) {
                sublocality3 = component.long_name;
              } else if (types.includes('sublocality_level_2')) {
                sublocality2 = component.long_name;
              } else if (types.includes('sublocality_level_1')) {
                sublocality1 = component.long_name;
              } else if (types.includes('locality')) {
                addressData.city = component.long_name;
              } else if (types.includes('administrative_area_level_2') && !addressData.city) {
                addressData.city = component.long_name;
              } else if (types.includes('postal_code')) {
                addressData.postalCode = component.long_name;
              } else if (types.includes('country')) {
                addressData.country = component.long_name;
              } else if (types.includes('administrative_area_level_1')) {
                addressData.state = component.long_name;
              }
            });

            // Construct clean address
            const streetAddress = [building, route, neighborhood, sublocality3, sublocality2, sublocality1]
              .filter(Boolean)
              .join(', ');
            addressData.address = streetAddress || place.formatted_address;

            // Call the onPlaceSelect callback with parsed data
            if (onPlaceSelect) {
              onPlaceSelect(addressData);
            }
          }
        }
      );
    }
  };

  const handleBlur = () => {
    // Delay hiding predictions to allow for clicks
    setTimeout(() => {
      setShowPredictions(false);
    }, 200);
  };

  const handleFocus = () => {
    if (predictions.length > 0) {
      setShowPredictions(true);
    }
  };

  // If no API key, show regular text field with warning
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Box position="relative" width="100%">
        <TextField
          ref={inputRef}
          fullWidth
          label={label}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          error={error}
          helperText={helperText || "Google Maps API key not configured. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file"}
        />
      </Box>
    );
  }

  return (
    <Box position="relative" width="100%">
      <TextField
        ref={inputRef}
        fullWidth
        label={label}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        inputProps={{ autoComplete: 'off' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {isLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      border: '2px solid #f3f3f3',
                      borderTop: '2px solid #3498db',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }}
                  />
                </Box>
              )}
              <Tooltip title="Select from Map">
                <IconButton
                  edge="end"
                  onClick={onMapOpen}
                  color="primary"
                  size="small"
                >
                  <LocationIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )
        }}
      />

      {showPredictions && predictions.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: 300,
            overflow: 'auto',
            mt: 1
          }}
        >
          <List dense>
            {predictions.map((prediction, index) => (
              <ListItem
                key={prediction.place_id}
                button
                onClick={() => handlePredictionClick(prediction)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemText
                  primary={prediction.structured_formatting?.main_text || prediction.description}
                  secondary={prediction.structured_formatting?.secondary_text}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default GooglePlacesAutocomplete;
